import { Component, Inject } from '@angular/core';
import { NzModalRef, NZ_MODAL_DATA } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-csv-upload-dialog',
  templateUrl: './csv-upload-dialog.component.html',
  styleUrls: ['./csv-upload-dialog.component.less'],
})
export class CsvUploadDialogComponent {
  uploadedFile: any = null;
  defaultFileType: string = 'xlsx';
  selectMode: string = 'append';
  selectedTemplateType: string = 'general';
  selectedMasterData: any = '';
  templateTypeOptions: any[] = [];
  filteredMasterDataOptions: any[] = [];
  allMasterDataOptions: any[] = [];
  dateRange: Date[] = (() => {
    const today = new Date();
    const day = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return [monday, sunday];
  })();
  isDateRangeValid = true;

  constructor(
    private modal: NzModalRef,
    private msg: NzMessageService,
    private http: HttpClient,
    @Inject(NZ_MODAL_DATA) public data: any
  ) {
    this.initializeOptions();
  }

  initializeOptions(): void {
    if (this.data?.templateTypeOptions) {
      this.templateTypeOptions = this.data.templateTypeOptions;
    }

    if (this.data?.masterDataOptions) {
      this.allMasterDataOptions = this.data.masterDataOptions;
      this.filteredMasterDataOptions = this.allMasterDataOptions.filter(item => !this.hasAnyAttribute(item));
    }
  }

  private hasAnyAttribute(item: any): boolean {
    for (let i = 1; i <= 10; i++) {
      if (item[`attr${i}`]) {
        return true;
      }
    }
    return false;
  }

  onTemplateTypeChange(): void {
    if (this.selectedTemplateType === 'general') {
      this.filteredMasterDataOptions = this.allMasterDataOptions.filter(item => !this.hasAnyAttribute(item));
    } else if (this.selectedTemplateType === 'specific') {
      this.filteredMasterDataOptions = this.allMasterDataOptions.filter(item => this.hasAnyAttribute(item));
    } else {
      this.filteredMasterDataOptions = [];
    }
    
    this.selectedMasterData = '';
    console.log('Template Type Changed:', this.selectedTemplateType);
    console.log('Filtered Master Data Options:', this.filteredMasterDataOptions);
  }

  onMasterDataChange(): void {
    console.log('Master Data Changed:', this.selectedMasterData);
  }

  validateDateRange(): void {
    if (!this.dateRange || this.dateRange.length !== 2) {
      this.isDateRangeValid = false;
      return;
    }
    const [from, to] = this.dateRange;
    const isMonday = from.getDay() === 1;
    const isSunday = to.getDay() === 0;
    const diffDays = Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
    this.isDateRangeValid = isMonday && isSunday && diffDays === 6;
  }

  getDownloadLink(): string {
    if (!this.selectedMasterData) {
      return this.data?.downloadSampleLink || '';
    }
    
    const selectedOption = this.filteredMasterDataOptions.find(opt => opt.id === this.selectedMasterData);
    if (selectedOption) {
      const baseLink = this.data?.downloadSampleLink || '';
      return `${baseLink}?refTypeId=${selectedOption.id}`;
    }
    
    return this.data?.downloadSampleLink || '';
  }

  getSelectedMasterDataDescription(): string {
    if (!this.selectedMasterData) {
      return '';
    }
    
    const selectedOption = this.filteredMasterDataOptions.find(opt => opt.id === this.selectedMasterData);
    return selectedOption ? selectedOption.description : '';
  }

  shouldShowDownloadLink(): boolean {
    if (this.data?.modalOf === 'masterdata') {
      return !!this.selectedMasterData;
    }
    return true;
  }

  getTruncatedText(text: string, maxLength: number): string {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }

  customUploadReq = (item: any): any => {
    const file = item.file;
    const fileType = this.data?.fileType || 'csv';
    const fileName = file.name.toLowerCase();

    const isCsv = file.type === 'text/csv' || fileName.endsWith('.csv');
    const isExcel =
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      fileName.endsWith('.xlsx');

    if (fileType === 'csv' && !isCsv) {
      this.msg.error('Only CSV files are allowed.');
      return;
    }

    if (fileType === 'excel' && !isExcel) {
      this.msg.error('Only Excel (.xlsx) files are allowed.');
      return;
    }

    if (fileType === 'csv|excel' && !isCsv && !isExcel) {
      this.msg.error('Only CSV or Excel (.xlsx) files are allowed.');
      return;
    }

    const fileSizeMB = file.size / 1024 / 1024;
    if (Math.ceil(fileSizeMB) > 1) {
      this.msg.error('File size exceeds 1 MB limit.');
      return;
    }

    this.uploadedFile = file;
    this.msg.success(`${file.name} selected successfully`);
  };

  onSubmit(): void {
    if (this.data?.modalOf === 'masterdata') {
      if (!this.selectedTemplateType) {
        this.msg.warning('Please select a Template Type');
        return;
      }
      if (!this.selectedMasterData) {
        this.msg.warning('Please select Master Data');
        return;
      }
      if (!this.uploadedFile) {
        this.msg.warning('Please select a file first');
        return;
      }
      
      const jobId = new Date().getTime();
      const response = {
        uploadFile: this.uploadedFile,
        refTypeId: this.selectedMasterData,
        jobId: jobId,
        templateType: this.selectedTemplateType
      };
      this.modal.close(response);
      return;
    }
    
    if (this.data?.modalOf === 'exchangerate') {
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.msg.warning('Please select Valid From & To dates');
        return;
      }
      if (!this.isDateRangeValid) {
        this.msg.warning('Start date must be a Monday and end date must be a Sunday');
        return;
      }
      if (!this.uploadedFile) {
        this.msg.warning('Please select a file first');
        return;
      }
      this.modal.close({ uploadFile: this.uploadedFile, dateRange: this.dateRange });
      return;
    }

    if (this.uploadedFile) {
      if (this.data?.modalOf === 'usermgmt') {
        const response = {
          uploadFile: this.uploadedFile,
          mode: this.selectMode
        };
        this.modal.close(response);
        return;
      }
      
      this.modal.close(this.uploadedFile);
    } else {
      this.msg.warning('Please select a file first');
    }
  }

  onCancel(): void {
    this.modal.close();
  }

  downloadSampleFile(fileType: 'csv' | 'excel' | 'csv|excel'): void {
    if (this.data?.downloadSampleFn) {
      this.data.downloadSampleFn();
      return;
    }
    if (fileType === 'excel' || fileType === 'csv|excel') {
      this.downloadTemplateFromAPI();
    } else {
      const csvData = 'data1\ndata2\ndata3';
      const blob = new Blob([csvData], { type: 'text/csv' });
      this.downloadFile(blob, 'sample.csv');
    }
  }

  downloadTemplateFromAPI(): void {
    // if (this.data?.downloadSampleFn) {
    //   this.data.downloadSampleFn();
    //   return;
    // }
    const description = this.getSelectedMasterDataDescription();
    const fileName = description 
      ? `MasterData_Template_${description}.${this.defaultFileType}`
      : (this.data?.sampleName ? this.data.sampleName : `Sample.${this.defaultFileType}`);
    
    this.downloadSample().subscribe({
        next: (blob: Blob) => this.downloadFile(blob, fileName),
        error: (error) => {
          this.msg.error('Failed to download template file');
          console.error('Download error:', error);
        }
      });
  }

  downloadSample(): Observable<Blob> {
    const downloadLink = this.getDownloadLink();
    
    if (!downloadLink) {
      this.msg.error('Download link is missing.');
      return of(new Blob());
    }
  
    if (this.data?.fileType === 'csv|excel') {
      let link = downloadLink;
      link = link + (link.includes('?') ? '&' : '?') + `format=${this.defaultFileType}`;
      return this.http.get(link, { responseType: 'blob' });
    }
    return this.http.get(downloadLink, { responseType: 'blob' });
  }

  private downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
